import { useEffect, useState } from "react";
import { api } from "@/lib/api";

// Module-level caches so the canonical LMS lists are fetched once and shared
// read-only across every PonderrLearn authoring form.
let _subjectsCache = null;
let _subjectsPromise = null;
let _gradesCache = null;
let _gradesPromise = null;

export function useMasterSubjects() {
  const [subjects, setSubjects] = useState(_subjectsCache || []);
  useEffect(() => {
    if (_subjectsCache) { setSubjects(_subjectsCache); return; }
    if (!_subjectsPromise) {
      _subjectsPromise = api.get("/global/lms/subjects")
        .then((r) => { _subjectsCache = r.data || []; return _subjectsCache; })
        .catch(() => { _subjectsCache = []; return []; });
    }
    _subjectsPromise.then((d) => setSubjects(d));
  }, []);
  return subjects;
}

export function useMasterGrades() {
  const [grades, setGrades] = useState(_gradesCache || []);
  useEffect(() => {
    if (_gradesCache) { setGrades(_gradesCache); return; }
    if (!_gradesPromise) {
      _gradesPromise = api.get("/global/master-grades")
        .then((r) => { _gradesCache = r.data || []; return _gradesCache; })
        .catch(() => { _gradesCache = []; return []; });
    }
    _gradesPromise.then((d) => setGrades(d));
  }, []);
  return grades;
}

// Subject dropdown bound to canonical Master Subjects, grouped by
// Scholastic / Non-Scholastic. Stored/selected value is the subject NAME so
// existing PonderrLearn records (which persist subject_name strings) stay valid.
export function SubjectSelect({ value, onChange, board, className = "nb-select", "data-testid": testid }) {
  const subjects = useMasterSubjects();
  const applies = (s) => !board || !(s.applicable_boards?.length) || s.applicable_boards.includes(board);
  const visible = subjects.filter(applies);
  const scholastic = visible.filter((s) => (s.subject_type || "Scholastic") === "Scholastic");
  const nonScholastic = visible.filter((s) => s.subject_type === "Non-Scholastic");
  const names = new Set(visible.map((s) => s.name));
  const legacy = value && !names.has(value);
  return (
    <select className={className} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      <option value="">Select subject…</option>
      {legacy && <option value={value}>{value}</option>}
      {scholastic.length > 0 && (
        <optgroup label="Scholastic">
          {scholastic.map((s) => <option key={s.subject_id} value={s.name}>{s.name}</option>)}
        </optgroup>
      )}
      {nonScholastic.length > 0 && (
        <optgroup label="Non-Scholastic">
          {nonScholastic.map((s) => <option key={s.subject_id} value={s.name}>{s.name}</option>)}
        </optgroup>
      )}
    </select>
  );
}

// Grade dropdown bound to canonical Master Grades. Value is the grade CODE.
export function GradeSelect({ value, onChange, className = "nb-select", "data-testid": testid }) {
  const grades = useMasterGrades();
  const codes = new Set(grades.map((g) => g.code));
  const legacy = value && !codes.has(value);
  return (
    <select className={className} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      {legacy && <option value={value}>{value}</option>}
      {grades.map((g) => <option key={g.code} value={g.code}>{g.name}</option>)}
    </select>
  );
}
